import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    AddressFormAutocomplete,
    AddressFormTelInput,
  } from "src/components/address";
import { CountryEnum, StateEnum, ShippingMethodEnum, CfaMethodEnum, ShippingMethodServiceEnum } from "src/common/enum";
import { CfaMethodProps } from "src/resources/sales/interface";
import {
  Button,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  PaperProps
} from "@mui/material";
import { useState } from "react";
import { useDataProvider, useRefresh } from "react-admin";
import Transformer from "src/common/Transformer";
import { useForm } from "react-hook-form";
import { SelectField } from "src/components/input";
import { phonePattern } from "src/common/UtilFunctions";
import Draggable from 'react-draggable';

interface CfaMethodFormModalProps {
  method: CfaMethodProps;
  open: boolean;
  setOpen: any;
  title: string;
  path: string;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const CfaMethodFormModal = (props: CfaMethodFormModalProps) => {
    const { method, open, setOpen, title, path } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { register, control, handleSubmit, getValues, setValue, formState: { errors }} = useForm<CfaMethodProps>({
        defaultValues: method
    });
    const dataProvider = useDataProvider();
    const refresh = useRefresh();

    const transformShippingMethod = (data) => {
        Transformer(data, {
        remove: ["createdAt", "id", "updatedAt", "description", "name"],
        update: {},
        });
        return data;
    };

    const transformShippingAddress = (data) => {
        Transformer(data, {
        remove: ["createdAt", "id", "updatedAt"],
        update: {},
        });
        return data;
    };

    const transform = (data) => {
        Transformer(data, {
        remove: ["createdAt", "id", "updatedAt", "description", "name"],
        update: {
            shippingMethod: transformShippingMethod({...data.shipping_method}),
            shippingAddress: transformShippingAddress({...data.shipping_address}),
        },
        });
        return data;
    };

    const update = async (data: CfaMethodProps) => {
        try {
            let formData = {...data};

            if (formData.type === "waived") {
                delete formData["shipping_method"]; 
                delete formData["shipping_address"]; 
            }        

            if (formData.shipping_method?.type !== "own_account") {
                delete formData["shipping_method"]["additional"]; 
            } 

            setLoading(true);
            setError(null);
            dataProvider
            .update(path, {
                id: "",
                data: transform({...formData}),
                previousData: method,
            })
            .then((res) => {
                setLoading(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(update)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={12} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Type"
                            size="small"
                            value={getValues("type") || ""}
                            error={!!errors.type}
                            helperText={(errors.type as any)?.message}
                            required
                            choices={CfaMethodEnum}
                            {...register("type", {
                                required: "Field is required"
                            })}
                            onChange={(e) => {
                               setValue('type', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>

                    {getValues("type") == "requested" &&
                    <>
                    <Grid item sm={12}>
                        <Typography component="h4" sx={{marginTop: "0px", marginBottom: "15px"}}>Shipping Method</Typography>
                        <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                            <Grid item sm={12} md={12} sx={{marginBottom: "18px"}}>
                                <SelectField
                                    label="Type"
                                    size="small"
                                    value={getValues("shipping_method.type") || ""}
                                    error={!!errors.shipping_method?.type}
                                    helperText={(errors.shipping_method?.type as any)?.message}
                                    required
                                    choices={ShippingMethodEnum}
                                    {...register("shipping_method.type", {
                                        required: "Field is required"
                                    })}
                                    onChange={(e) => {
                                        setValue('shipping_method.type', e.target.value, { shouldValidate: true });
                                    }}
                                />
                            </Grid>  

                            {getValues("shipping_method.type") == "own_account" &&
                            <>
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Courier"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors?.shipping_method?.additional?.courier}
                                    helperText={errors.shipping_method?.additional?.courier?.message}
                                    required={getValues("shipping_method.type") === "own_account" && true}
                                    {...register("shipping_method.additional.courier", {
                                        required: getValues("shipping_method.type") === "own_account" ? "Field is required" : false
                                    })}
                                />
                            </Grid>
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Account"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors?.shipping_method?.additional?.account_number}
                                    helperText={errors?.shipping_method?.additional?.account_number?.message}
                                    required={getValues("shipping_method.type") === "own_account" && true}
                                    {...register("shipping_method.additional.account_number", {
                                        required: getValues("shipping_method.type") === "own_account" ? "Field is required" : false
                                    })}
                                />
                            </Grid>
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <SelectField
                                    label="Service"
                                    size="small"
                                    value={getValues("shipping_method.additional.service") || ""}
                                    error={!!errors?.shipping_method?.additional?.service}
                                    helperText={errors?.shipping_method?.additional?.service?.message}
                                    required={getValues("shipping_method.type") === "own_account" && true}
                                    choices={ShippingMethodServiceEnum}
                                    {...register("shipping_method.additional.service", {
                                        required: getValues("shipping_method.type") === "own_account" ? "Field is required" : false
                                    })}
                                    onChange={(e) => {
                                        setValue('shipping_method.additional.service', e.target.value, { shouldValidate: true });
                                    }}
                                />
                            </Grid>
                            </>
                            }
                        </Grid>
                    </Grid>

                    <Grid item sm={12}>
                        <Typography component="h4" sx={{marginTop: "0px", marginBottom: "15px"}}>Shipping Address</Typography>
                        <Grid container item sm={12} rowSpacing={1} columnSpacing={2}> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="First Name"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.first_name}
                                    required
                                    helperText={errors.shipping_address?.first_name?.message}
                                    {...register("shipping_address.first_name", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Last Name"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.last_name}
                                    required
                                    helperText={errors.shipping_address?.last_name?.message}
                                    {...register("shipping_address.last_name", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Email"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.email}
                                    helperText={errors.shipping_address?.email?.message}
                                    {...register("shipping_address.email", {
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: "Entered value does not match email format"
                                        }
                                    })}
                                />
                            </Grid> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Company"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.company}
                                    helperText={errors.shipping_address?.company?.message}
                                    {...register("shipping_address.company", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid>
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Street Address"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.street_address}
                                    required
                                    helperText={errors.shipping_address?.street_address?.message}
                                    {...register("shipping_address.street_address", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid> 
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="City"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.city}
                                    required
                                    helperText={errors.shipping_address?.city?.message}
                                    {...register("shipping_address.city", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid> 
                            <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                                <TextField
                                    label="Zip"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.shipping_address?.zip}
                                    required
                                    helperText={errors.shipping_address?.zip?.message}
                                    {...register("shipping_address.zip", {
                                        required: "Field is required"
                                    })}
                                />
                            </Grid>
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <AddressFormAutocomplete
                                    value={getValues("shipping_address.country")}
                                    label={"Country"}
                                    size="small"
                                    required
                                    error={!!errors.shipping_address?.country}
                                    helperText={errors.shipping_address?.country?.message}
                                    options={CountryEnum}
                                    {...register("shipping_address.country", {
                                        required: "Field is required",
                                    })}
                                    onChange={(e, value) => {
                                        setValue('shipping_address.country', value.id, { shouldValidate: true });
                                        if (value.id !== "US") {
                                            setValue('shipping_address.state', '', { shouldValidate: true });
                                        }
                                    }}
                                />
                            </Grid> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <AddressFormAutocomplete
                                    disabled={getValues("shipping_address.country") !== "US"}
                                    value={getValues("shipping_address.state")}
                                    label={"State"}
                                    size="small"
                                    error={!!errors.shipping_address?.state}
                                    helperText={errors.shipping_address?.state?.message}
                                    options={StateEnum}
                                    {...register("shipping_address.state")}
                                    onChange={(e, value) => {
                                        setValue('shipping_address.state', value.id, { shouldValidate: true });
                                    }}
                                />
                            </Grid>

                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <AddressFormTelInput
                                    value={getValues("shipping_address.phone")}
                                    label="Phone"
                                    error={!!errors.shipping_address?.phone}
                                    helperText={errors.shipping_address?.phone?.message}
                                    required
                                    {...register("shipping_address.phone", {
                                        required: "Field is required",
                                        pattern: {
                                            value: phonePattern,
                                            message: "Entered value does not match phone format"
                                        }
                                    })}
                                    onChange={(value) => {
                                        setValue('shipping_address.phone', value, { shouldValidate: true });
                                    }}
                                />
                            </Grid> 
                            <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                                <AddressFormTelInput
                                    value={getValues("shipping_address.mobile")}
                                    label="Mobile"
                                    error={!!errors.shipping_address?.mobile}
                                    helperText={errors.shipping_address?.mobile?.message}
                                    {...register("shipping_address.mobile", {
                                        pattern: {
                                            value: phonePattern,
                                            message: "Entered value does not match phone format"
                                        }
                                    })}
                                    onChange={(value) => {
                                        setValue('shipping_address.mobile', value, { shouldValidate: true });
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    </>
                    }

                    { error &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{error}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Update
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
